/**
 * Error Normalization Layer
 * 
 * Maps backend error messages to user-friendly Romanian messages.
 * This ensures users never see technical error codes or English messages.
 */

// Exact match mapping for known backend error messages
const ERROR_MAP: Record<string, string> = {
  // Authentication & Authorization Errors
  "Missing or invalid authorization header": "Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.",
  "Unauthorized": "Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.",
  "Invalid token": "Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.",
  "Token expired": "Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.",
  "Session expired": "Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.",
  
  // Link Already Used Errors
  "This feedback link has already been used": "Acest link a fost deja folosit.",
  "Link already used": "Acest link a fost deja folosit.",
  "Conflict": "Acest link a fost deja folosit.",
  
  // Link Expired Errors
  "Link expired": "Acest link a expirat.",
  "This link has expired": "Acest link a expirat.",
  
  // Rate Limiting Errors
  "Too many validation attempts, please try again later": "Prea multe încercări. Vă rugăm să încercați mai târziu.",
  "Too many requests": "Prea multe încercări. Vă rugăm să încercați mai târziu.",
  "Rate limit exceeded": "Prea multe încercări. Vă rugăm să încercați mai târziu.",
  
  // Validation Errors
  "ValidationError": "Datele trimise nu sunt valide.",
  "Invalid request payload": "A apărut o eroare. Vă rugăm să reîncercați.",
  "Bad Request": "A apărut o eroare. Vă rugăm să reîncercați.",
  "Invalid input": "Datele trimise nu sunt valide.",
  
  // Network & Server Errors
  "Network error": "Eroare de conexiune. Verificați conexiunea la internet.",
  "Internal Server Error": "A apărut o eroare pe server. Vă rugăm să reîncercați.",
  "Service Unavailable": "Serviciul este momentan indisponibil. Vă rugăm să reîncercați.",
  "Gateway Timeout": "Conexiunea a expirat. Vă rugăm să reîncercați.",
  
  // Not Found Errors
  "Not Found": "Link invalid sau expirat.",
  "Link not found": "Link invalid sau expirat.",
  "Resource not found": "Link invalid sau expirat.",
};

// Keywords to search for partial matches (case-insensitive)
const KEYWORD_MAP: Array<{ keywords: string[]; message: string }> = [
  {
    keywords: ["unauthorized", "authorization", "not authorized", "missing authorization"],
    message: "Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.",
  },
  {
    keywords: ["already used", "already been used", "conflict"],
    message: "Acest link a fost deja folosit.",
  },
  {
    keywords: ["expired", "has expired"],
    message: "Acest link a expirat.",
  },
  {
    keywords: ["rate limit", "too many", "many requests", "many attempts"],
    message: "Prea multe încercări. Vă rugăm să încercați mai târziu.",
  },
  {
    keywords: ["validation", "invalid", "bad request"],
    message: "Datele trimise nu sunt valide.",
  },
  {
    keywords: ["not found", "404"],
    message: "Link invalid sau expirat.",
  },
  {
    keywords: ["network", "connection", "fetch"],
    message: "Eroare de conexiune. Verificați conexiunea la internet.",
  },
  {
    keywords: ["server error", "internal error", "500"],
    message: "A apărut o eroare pe server. Vă rugăm să reîncercați.",
  },
  {
    keywords: ["timeout", "timed out"],
    message: "Conexiunea a expirat. Vă rugăm să reîncercați.",
  },
];

// Default fallback message for unknown errors
const DEFAULT_ERROR_MESSAGE = "Ne pare rău, a apărut o eroare neașteptată. Vă rugăm să reîncercați.";

/**
 * Normalizes backend error messages to user-friendly Romanian messages
 * 
 * @param message - Raw error message from backend (can be in English or any format)
 * @returns User-friendly Romanian error message
 */
export function normalizeErrorMessage(message: string | undefined | null): string {
  // Handle empty or null messages
  if (!message || typeof message !== 'string' || message.trim() === '') {
    return DEFAULT_ERROR_MESSAGE;
  }

  const trimmedMessage = message.trim();

  // 1. Try exact match (case-sensitive)
  if (ERROR_MAP[trimmedMessage]) {
    return ERROR_MAP[trimmedMessage];
  }

  // 2. Try case-insensitive exact match
  const lowerMessage = trimmedMessage.toLowerCase();
  for (const [key, value] of Object.entries(ERROR_MAP)) {
    if (key.toLowerCase() === lowerMessage) {
      return value;
    }
  }

  // 3. Try keyword-based partial matching
  for (const { keywords, message: mappedMessage } of KEYWORD_MAP) {
    for (const keyword of keywords) {
      if (lowerMessage.includes(keyword.toLowerCase())) {
        return mappedMessage;
      }
    }
  }

  // 4. If message is already in Romanian (contains Romanian characters or known Romanian words)
  // Return it as-is (assuming backend already sent a friendly message)
  const romanianIndicators = ['ă', 'â', 'î', 'ș', 'ț', 'ţ', 'ş', 'rugăm', 'vă', 'deja', 'expirat'];
  const hasRomanianChars = romanianIndicators.some(indicator => lowerMessage.includes(indicator));
  
  if (hasRomanianChars) {
    return trimmedMessage;
  }

  // 5. Fallback to default generic message
  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Gets a user-friendly error message based on HTTP status code
 * 
 * @param statusCode - HTTP status code
 * @returns User-friendly Romanian error message
 */
export function getErrorMessageByStatusCode(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return "Datele trimise nu sunt valide.";
    case 401:
      return "Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.";
    case 403:
      return "Acest link nu este valid sau a fost deja folosit.";
    case 404:
      return "Link invalid sau expirat.";
    case 409:
      return "Acest link a fost deja folosit.";
    case 429:
      return "Prea multe încercări. Vă rugăm să încercați mai târziu.";
    case 500:
      return "A apărut o eroare pe server. Vă rugăm să reîncercați.";
    case 502:
    case 503:
      return "Serviciul este momentan indisponibil. Vă rugăm să reîncercați.";
    case 504:
      return "Conexiunea a expirat. Vă rugăm să reîncercați.";
    default:
      return DEFAULT_ERROR_MESSAGE;
  }
}
